import { ContentLink, useContentLink } from "@robe/client/xcore-ui";
import { SubCategory } from "@robe/core";
import { Box, Button, Container, Flex, SimpleGrid, Tag } from "@xcorejs/ui";
import NewsCarousel from "components/News/NewsDetail/NewsCarousel";
import NewsCard, { NewsCardNewsValuesFields } from "design-system/robe/Cards/News";
import RobeHeading from "design-system/robe/RobeHeading";
import { ComponentProps, ReactNode } from "react";
import { useLayout } from "xcore";
import { News } from "xcore/types";

interface NewsSectionProps {
  title: ReactNode;
  categories?: SubCategory[];
  news: News<NewsCardNewsValuesFields>[];
  buttonText: ReactNode;
  buttonLink: ComponentProps<typeof ContentLink>;
  backTo?: number;
}

const NewsSection = ({
  title,
  news,
  categories = [],
  buttonText,
  buttonLink: { content, queries, ...buttonLinkProps },
  backTo,
}: NewsSectionProps) => {
  const { header, stringify } = useLayout();
  const { as } = useContentLink();

  const queriesResolved =
    queries || backTo
      ? {
        queries: {
          ...(queries && { ...queries }),
          ...(backTo && { backTo }),
        },
      }
      : {};

  const buttonLinkUrl = as(content, { ...queriesResolved, ...buttonLinkProps });

  // More than 8 items shifts the carousel to the sides and creates horizontal scroll in mobile version
  const newsLimited = news.slice(-8);

  return (
    <Box
      bg="robe"
      background={{
        _: "url(/static/news/news-767.jpg)",
        sm: "url(/static/news/news-1024.jpg)",
        md: "url(/static/news/news-1440.jpg)",
        lg: "url(/static/news/news-1920.jpg)",
        xl: "url(/static/news/news-1920.jpg)",
      }}
      backgroundRepeat="none"
      backgroundPosition="center"
      py={{ _: "5rem", sm: "8rem" }}
    >
      <Container flexDirection="column" alignItems="center">
        <RobeHeading mb="2rem" color="#fff">
          {title}
        </RobeHeading>

        <Flex mb="4rem" maxWidth="70rem" justifyContent="center" alignItems="center" display={{ _: "none", sm: "block" }}>
          <Flex justifyContent="center" flexWrap="wrap" margin="-0.5rem">
            {categories.map((n) => (
              <ContentLink content={header.nav.newsPage} key={n.id} queries={{ category: n.id }} variant="simple">
                <Tag variant="outline" {...TagStyle}>
                  {stringify(n.name)}
                </Tag>
              </ContentLink>
            ))}
          </Flex>
        </Flex>

        <Flex mb="6rem" width="100%" justifyContent="center" display={{ _: "none", sm: "flex" }}>
          <SimpleGrid gap="3rem" columns={{ _: 1, sm: 2, lg: 4 }} column="minmax(100px, 300px)" width={{ _: "auto", sm: "100%" }}>
            {newsLimited.map((c) => (
              <ContentLink
                noUILink
                style={{ width: "100%", height: "100%" }}
                key={c.id}
                content={c}
                {...(backTo && { queries: { backto: backTo } })}
                variant="simple"
              >
                <NewsCard content={c} variant="default" />
              </ContentLink>
            ))}
          </SimpleGrid>
        </Flex>

        <Box width="100%" display={{ _: "block", sm: "none" }}>
          <NewsCarousel news={newsLimited} backTo={backTo} />
        </Box>

        {buttonLinkUrl && (
          <a href={buttonLinkUrl} {...buttonLinkProps}>
            <Button mb={{ _: "-6rem", sm: 0 }} mt={{ _: "-2rem", sm: 0 }}>
              {buttonText}
            </Button>
          </a>
        )}
      </Container>
    </Box>
  );
};

export default NewsSection;

const TagStyle = {
  margin: "0.5rem",
  cursor: "pointer",
  transition: "background 300ms",
  color: "#fff",

  _hover: {
    background: "#5E6062",
  },
};
